import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { expenseAPI } from '../lib/api';
import { Type, Camera, Mic, Upload, Loader2, Send, MicOff } from 'lucide-react';

export const AddExpense = ({ onExpenseAdded, categories }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('text');
  const [textInput, setTextInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Handle subscription-related errors
  const handleApiError = (error, context) => {
    if (error.response?.status === 403) {
      const message = error.response?.data?.detail || '';
      if (message.includes('limit')) {
        toast.error('You\'ve reached your free plan limit. Upgrade to add more expenses.', {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/pricing'),
          },
        });
      } else if (message.includes('Receipt') || message.includes('scan')) {
        toast.error('Receipt scanning is a Pro feature. Upgrade to scan receipts.', {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/pricing'),
          },
        });
      } else {
        toast.error(message || 'This feature requires a Pro subscription.', {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/pricing'),
          },
        });
      }
    } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      toast.error('Upload timed out. Please try with a smaller image or check your connection.');
    } else if (error.message === 'Network Error') {
      toast.error('Network error. Please check your internet connection and try again.');
    } else {
      toast.error(error.response?.data?.detail || `Failed to ${context}`);
    }
  };

  // Text input submission
  const handleTextSubmit = async () => {
    if (!textInput.trim()) {
      toast.error('Please enter an expense description');
      return;
    }

    setLoading(true);
    try {
      const response = await expenseAPI.createFromText(textInput);
      toast.success(`Expense added: $${response.data.amount.toFixed(2)} - ${response.data.description}`);
      setTextInput('');
      onExpenseAdded?.(response.data);
    } catch (error) {
      handleApiError(error, 'process expense');
    } finally {
      setLoading(false);
    }
  };

  // Receipt scan
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Image is too large. Please use an image under 10MB.');
      return;
    }

    setLoading(true);
    toast.info(`Uploading ${file.name}...`);
    
    try {
      console.log('Starting receipt upload...');
      const response = await expenseAPI.createFromReceipt(file);
      console.log('Receipt upload response:', response.data);
      const expenseDate = response.data.date || 'today';
      toast.success(`Receipt scanned: $${response.data.amount.toFixed(2)} - ${response.data.description || 'Expense added'} (Date: ${expenseDate})`);
      onExpenseAdded?.(response.data);
    } catch (error) {
      console.error('Receipt upload error:', error);
      console.error('Error response:', error.response);
      handleApiError(error, 'scan receipt');
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Voice input using Web Speech API
  const startVoiceRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Voice input is not supported in your browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;
    recognitionRef.current.lang = 'en-CA';

    recognitionRef.current.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setTextInput(transcript);
      setIsRecording(false);
      toast.success('Voice captured! Review and submit.');
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      toast.error('Voice recognition failed. Please try again.');
      setIsRecording(false);
    };

    recognitionRef.current.onend = () => {
      setIsRecording(false);
    };

    recognitionRef.current.start();
    setIsRecording(true);
    setActiveTab('text'); // Switch to text tab to show result
  };

  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
  };

  return (
    <Card className="bg-card border-white/10" data-testid="add-expense-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold tracking-tight">Add Expense</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 bg-secondary/50 p-1 rounded-lg">
            <TabsTrigger
              value="text"
              className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md"
              data-testid="text-tab"
            >
              <Type className="w-4 h-4 mr-2" />
              Text
            </TabsTrigger>
            <TabsTrigger
              value="scan"
              className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md"
              data-testid="scan-tab"
            >
              <Camera className="w-4 h-4 mr-2" />
              Scan
            </TabsTrigger>
            <TabsTrigger
              value="voice"
              className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary rounded-md"
              data-testid="voice-tab"
            >
              <Mic className="w-4 h-4 mr-2" />
              Voice
            </TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="mt-4 space-y-4">
            <Textarea
              placeholder="e.g., 'Spent $25 on sushi for lunch'"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              className="min-h-[100px] bg-secondary/30 border-white/10 focus:border-primary resize-none"
              data-testid="text-expense-input"
            />
            <Button
              onClick={handleTextSubmit}
              disabled={loading || !textInput.trim()}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
              data-testid="track-expense-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Track Expense
            </Button>
          </TabsContent>

          <TabsContent value="scan" className="mt-4 space-y-4">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              ref={fileInputRef}
              className="hidden"
              data-testid="receipt-file-input"
            />
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  fileInputRef.current?.setAttribute('capture', 'environment');
                  fileInputRef.current?.click();
                }}
                disabled={loading}
                className="scan-area rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover-lift"
                data-testid="take-photo-btn"
              >
                <Camera className="w-8 h-8 text-primary" />
                <span className="text-sm text-muted-foreground">Take Photo</span>
              </button>
              <button
                onClick={() => {
                  fileInputRef.current?.removeAttribute('capture');
                  fileInputRef.current?.click();
                }}
                disabled={loading}
                className="scan-area rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover-lift"
                data-testid="choose-photo-btn"
              >
                <Upload className="w-8 h-8 text-blue-400" />
                <span className="text-sm text-muted-foreground">Choose Photo</span>
              </button>
            </div>
            {loading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">Processing receipt...</span>
              </div>
            )}
          </TabsContent>

          <TabsContent value="voice" className="mt-4 space-y-4">
            <div className="flex flex-col items-center justify-center py-8">
              <button
                onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                disabled={loading}
                className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                  isRecording
                    ? 'bg-destructive recording-pulse'
                    : 'bg-primary/20 hover:bg-primary/30'
                }`}
                data-testid="voice-record-btn"
              >
                {isRecording ? (
                  <MicOff className="w-10 h-10 text-white" />
                ) : (
                  <Mic className="w-10 h-10 text-primary" />
                )}
              </button>
              <p className="mt-4 text-sm text-muted-foreground">
                {isRecording ? 'Listening... Tap to stop' : 'Tap to start speaking'}
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
